import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
import * as echarts from 'echarts';
import { ref, watch, onMounted, onUnmounted } from 'vue';

/**
 * 组件props定义
 */

export default {
  __name: 'index',
  props: {
    // IP地址
    name: String,
    // ping结果数据数组
    results: {
      type: Array,
      default: () => []
    },
    // 是否显示完整图表（用于历史查询页面）
    showAll: {
      type: Boolean,
      default: false
    },
    // 图表高度
    height: {
      type: String,
      default: ''
    }
  },
  setup(__props) {
    const props = __props;

    /**
     * 图表DOM元素引用
     * @type {import('vue').Ref<HTMLElement|null>}
     */
    const chartRef = ref(null);

    /**
     * ECharts图表实例
     * @type {Object|null}
     */
    let chart = null;

    /**
     * 图表是否已销毁标记
     * @type {boolean}
     */
    let isDisposed = false;

    /**
     *  resizeObserver实例
     * @type {ResizeObserver|null}
     */
    let resizeObserver = null;

    /**
     * 初始化图表
     */
    const initChart = () => {
      if (!chartRef.value || isDisposed) return;
      chart = echarts.init(chartRef.value);
      updateChart();
    };

    /**
     * 更新图表数据
     */
    const updateChart = () => {
      if (!chart || isDisposed || !chartRef.value) return;
      const data = props.results || [];

      // 将时间戳转换为显示格式
      const timestamps = data.map(r => {
        const ts = r.createdAt || r.timestamp;
        if (!ts) return 'N/A';
        let date;
        if (typeof ts === 'string') {
          date = new Date(ts.replace(' ', 'T'));
        } else if (ts instanceof Date) {
          date = ts;
        } else {
          return 'N/A';
        }
        if (isNaN(date.getTime())) return 'N/A';
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
      });

      // 延迟数据，失败时为null（显示断点）
      const latencies = data.map(r => r.success ? r.latency : null);
      const option = {
        title: {
          text: props.showAll ? `${props.name} (网络状态)` : '',
          left: 'center',
          textStyle: {
            fontSize: 14
          }
        },
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const p = params[0];
            if (p.value == null) return `${p.name}<br/>状态: 离线`;
            return `${p.name}<br/>延迟: ${p.value}ms`;
          }
        },
        grid: {
          left: '8%',
          right: '5%',
          top: props.showAll ? '18%' : '10%',
          bottom: '10%'
        },
        xAxis: {
          type: 'category',
          data: timestamps,
          axisLabel: {
            rotate: 0,
            fontSize: 11,
            interval: props.showAll ? Math.floor(timestamps.length / 12) : 'auto',
            showMaxLabel: true,
            showMinLabel: true
          }
        },
        yAxis: {
          type: 'value',
          name: 'ms',
          min: 0,
          axisLabel: {
            fontSize: 10
          }
        },
        series: [{
          type: 'line',
          data: latencies,
          smooth: true,
          symbol: 'circle',
          symbolSize: props.showAll ? 4 : 6,
          lineStyle: {
            color: '#1890ff',
            width: 2
          },
          itemStyle: {
            color: '#1890ff'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
              offset: 0,
              color: 'rgba(24,144,255,0.3)'
            }, {
              offset: 1,
              color: 'rgba(24,144,255,0.05)'
            }])
          }
        }]
      };
      chart.setOption(option);
    };

    // 监听results变化，自动更新图表
    watch(() => props.results, () => {
      updateChart();
      // 数据更新后重新调整图表大小
      setTimeout(() => chart?.resize(), 100);
    }, {
      deep: true
    });

    // 组件挂载时初始化图表
    onMounted(() => {
      initChart();
      // 监听窗口大小变化，自动调整图表大小
      window.addEventListener('resize', () => chart?.resize());

      // 监听容器大小变化，自动调整图表大小
      resizeObserver = new ResizeObserver(() => {
        chart?.resize();
      });
      if (chartRef.value) {
        resizeObserver.observe(chartRef.value);
      }
    });

    // 组件卸载时销毁图表
    onUnmounted(() => {
      isDisposed = true;
      chart?.dispose();
      // 取消容器大小变化监听
      resizeObserver?.disconnect();
    });
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", {
        class: "latency-chart",
        ref_key: "chartRef",
        ref: chartRef,
        style: _normalizeStyle({
          height: __props.height || (__props.showAll ? '400px' : '176px')
        })
      }, null, 4);
    };
  }
};